import { html, LitElement } from "lit";
import {
  customElement,
  state,
  property,
  query,
  queryAssignedNodes,
} from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import {
  Sizes,
  SizeResolverService as sizeResolver,
} from "@slhuicore/utils/size.js";

import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { SlhNavigationItem } from "@slhui/navigation-item";
import { style } from "./assets/navigation.generated-style";
import {
  EVENT_NAVIGATION_CLICK,
  MOVE_DIRECTION_LEFT,
  MOVE_DIRECTION_RIGHT,
  NavigationSize,
} from "./navigation.constants";

/**
 * @CustomElement slh-navigation
 *
 * @cssprop --slh-default-font-family - navigation font family.
 * @cssprop --slh-base-semi-bold-font-weight - navigation selected item font weight.
 * @cssprop --slh-base-m-font-size - navigation selected item font size.
 * @cssprop --slh-base-xxs-line-height - navigation selected item font size.
 * @cssprop --slh-color-neutral-shade-00 - navigation background.
 * @cssprop --slh-color-neutral-shade-20 - navigation bottom border.
 * @cssprop --slh-color-neutral-shade-40 - navigation arrow icon fill.
 * @cssprop --slh-color-neutral - navigation active color.
 *
 *  * ** slots
 * @slot default - List of slh-navigation-items
 */
@customElement("slh-navigation")
export default class SlhNavigation extends LitElement {
  @query(".items-container", false)
  protected itemsContainerElement: HTMLElement | undefined;

  @queryAssignedNodes()
  protected defaultSlotNodes: Array<HTMLElement> | undefined;

  /**
   * Sets top position of expanded navigation items container on mobile screen resolution (xs|sm breakpoints)
   */
  @property({
    type: Number,
    attribute: true,
  })
  top: number = 0;

  /**
   * Extends the navigation across all viewport and device sizes, up to 1920px
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  fluid: boolean = false;

  /**
   * Applies left/right padding size to the component. Used only for fluid navigation.
   */
  @property({
    type: NavigationSize,
    attribute: true,
  })
  padding: NavigationSize = NavigationSize.MD;

  @state()
  activeItemLabel: string = "";

  @state()
  opened: boolean = false;

  private toggleMobileNavigation: () => void = () => {};

  private startMoving: (e: MouseEvent | TouchEvent) => void = () => {};

  private stopMoving: (e: MouseEvent | TouchEvent) => void = () => {};

  private mouseMove: (e: MouseEvent | TouchEvent) => void = () => {};

  private onOutsideClick: (this: Document, e: MouseEvent) => any = () => {};

  private mouseDown: boolean = false;

  private startX: any;

  private containerScrollLeft: any;

  static styles = style;

  connectedCallback() {
    super.connectedCallback();
    this.onOutsideClick = this.outsideClick.bind(this);
    this.toggleMobileNavigation = this.toggleNavigation.bind(this, false);
    window.addEventListener("resize", this.toggleMobileNavigation);
  }

  protected firstUpdated(): void {
    this.startMoving = this.mouseStartMoving.bind(this);
    this.stopMoving = this.mouseStopMoving.bind(this);
    this.mouseMove = this.mouseMoveEvent.bind(this);

    this.itemsContainerElement?.addEventListener("mousedown", this.startMoving);
    this.itemsContainerElement?.addEventListener("mouseup", this.stopMoving);
    this.itemsContainerElement?.addEventListener("mouseleave", this.stopMoving);
    this.itemsContainerElement?.addEventListener("mousemove", this.mouseMove);

    this.itemsContainerElement?.addEventListener(
      "touchstart",
      this.startMoving
    );
    this.itemsContainerElement?.addEventListener("touchend", this.stopMoving);
    this.itemsContainerElement?.addEventListener(
      "touchcancel",
      this.stopMoving
    );
    this.itemsContainerElement?.addEventListener("touchmove", this.mouseMove);

    window.setTimeout(() => {
      this.defaultSlotNodes?.forEach((item: HTMLElement) => {
        const navigationItem = item as SlhNavigationItem;
        if (navigationItem.active) {
          this.moveToActive(navigationItem);
        }
        item.addEventListener("dragstart", this.preventDrag);
      });
    }, 1);
  }

  // eslint-disable-next-line class-methods-use-this
  private preventDrag(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  /**
   * Close navigation on outside click
   *
   * @param {MouseEvent} event
   */
  outsideClick(event: MouseEvent) {
    if (!event.composedPath().includes(this.renderRoot)) {
      this.opened = false;
      document.body.style.overflowY = "auto";
    }
  }

  private moveToActive(navigationItem: SlhNavigationItem) {
    if (this.itemsContainerElement && this.canScroll()) {
      if (navigationItem.offsetLeft > this.itemsContainerElement.offsetWidth) {
        this.itemsContainerElement.scrollLeft =
          navigationItem.offsetLeft -
          this.itemsContainerElement.offsetWidth / 2 +
          navigationItem.offsetWidth;
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private getPageX(event: MouseEvent | TouchEvent) {
    let pageX;
    if (event instanceof MouseEvent) {
      pageX = event.pageX;
    } else {
      pageX = event.targetTouches[0].pageX;
    }
    return pageX;
  }

  private mouseMoveEvent(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this.canScroll() && this.mouseDown && this.itemsContainerElement) {
      const x = this.getPageX(event) - this.itemsContainerElement.offsetLeft;
      const scroll = x - this.startX;
      this.itemsContainerElement.scrollLeft = this.containerScrollLeft - scroll;
    }
  }

  private mouseStartMoving(event: MouseEvent | TouchEvent) {
    if (this.itemsContainerElement && this.canScroll()) {
      this.mouseDown = true;
      this.startX =
        this.getPageX(event) - this.itemsContainerElement.offsetLeft;
      this.containerScrollLeft = this.itemsContainerElement.scrollLeft;
    }
  }

  private mouseStopMoving(event: MouseEvent | TouchEvent) {
    if (this.itemsContainerElement && this.canScroll()) {
      const endX = this.getPageX(event) - this.itemsContainerElement.offsetLeft;
      let direction = MOVE_DIRECTION_RIGHT;
      if (this.startX > endX) {
        // swipe left
        direction = MOVE_DIRECTION_LEFT;
      }
      this.showHalfVisibleItem(direction);
    }
    this.mouseDown = false;
  }

  // eslint-disable-next-line class-methods-use-this
  canScroll(): boolean {
    sizeResolver.checkWindowSize();
    const size = sizeResolver.getSize();
    if (size >= Sizes.SM) {
      return true;
    }
    return false;
  }

  private showHalfVisibleItem(direction: string) {
    this.defaultSlotNodes?.forEach((item: HTMLElement) => {
      if (
        this.itemsContainerElement &&
        item.nodeName.endsWith("NAVIGATION-ITEM")
      ) {
        const navigationItem = item as SlhNavigationItem;
        const itemLeft =
          navigationItem.offsetLeft - this.itemsContainerElement.offsetLeft;
        const itemLeftAndWith =
          navigationItem.offsetLeft +
          navigationItem.offsetWidth -
          this.itemsContainerElement.offsetLeft;
        let containerLeft = this.itemsContainerElement.scrollLeft;
        let scrollTo = itemLeft;

        if (direction === MOVE_DIRECTION_LEFT) {
          containerLeft =
            this.itemsContainerElement.scrollLeft +
            this.itemsContainerElement.offsetWidth;
          scrollTo = itemLeftAndWith - this.itemsContainerElement.offsetWidth;
        }

        if (containerLeft < itemLeftAndWith && containerLeft > itemLeft) {
          this.itemsContainerElement.scrollLeft = scrollTo;
        }
      }
    });
  }

  /**
   * @fires "click"
   * @param {boolean} opened
   */
  private toggleNavigation(opened: boolean) {
    const customEvent = new CustomEvent(EVENT_NAVIGATION_CLICK, {
      detail: {
        opened,
        tag: this.tagName,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
    if (this.opened !== opened) {
      this.opened = opened;
      if (this.opened) {
        document.addEventListener("click", this.onOutsideClick);
        document.body.style.overflowY = "hidden";
      }
    }
    if (!this.opened) {
      document.body.style.overflowY = "auto";
      document.removeEventListener("click", this.onOutsideClick);
    }
  }

  private onSlotChange({ target }: Event & { target: HTMLSlotElement }): void {
    const items = target.assignedElements() as SlhNavigationItem[];
    items.forEach((item: SlhNavigationItem) => {
      if (item.active) {
        this.activeItemLabel = item.innerHTML;
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onOutsideClick);
    window.removeEventListener("resize", this.toggleMobileNavigation);
  }

  /**
   * @returns {ClassInfo} ClassList
   */
  renderClasses(): ClassInfo {
    return {
      fluid: this.fluid,
      "padding-sm": this.fluid && this.padding === NavigationSize.SM,
      "padding-md": this.fluid && this.padding === NavigationSize.MD,
    };
  }

  render() {
    return html`<div
      class="slh-navigation ${classMap({ opened: this.opened })}"
      style="top: ${this.getTopPosition()}px;"
    >
      <div
        class="active-navigation-item"
        @click="${this.toggleNavigation.bind(this, !this.opened)}"
      >
        <span>${this.activeItemLabel}</span>

        <slh-icon type="${IconType.ICO_ARROW}"></slh-icon>
      </div>
      <div class="items-container ${classMap(this.renderClasses())}">
        <slot @slotchange="${this.onSlotChange}"></slot>
      </div>
    </div>`;
  }

  /**
   * Returns top position of mobile navigation container
   */
  private getTopPosition(): number {
    sizeResolver.checkWindowSize();
    const size = sizeResolver.getSize();
    return size === Sizes.XS || size === Sizes.SM ? this.top : 0;
  }
}
